// =============================================================================
// AnahReferencesBlock — badges multi-références ANAH d'un dossier
// =============================================================================
// Source canonique : dossier.anah_references (List[str]) — depuis S1 2026-05-28.
// Legacy reference_anah + anah_reference préservés (lecture seule, sync ANAH).
// PATCH /api/metier/dossiers/{id} body {anah_references: [...]}
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { Hash, Plus, X, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const REF_RE = /^\d{4,10}$/;

export default function AnahReferencesBlock({
  dossierId,
  tenantId,
  initialRefs = [],
  readOnly = false,
  onUpdated,
}) {
  const [refs, setRefs] = useState(Array.isArray(initialRefs) ? initialRefs : []);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setRefs(Array.isArray(initialRefs) ? initialRefs : []);
  }, [initialRefs]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    const userId = localStorage.getItem("user_id");
    const userRole = localStorage.getItem("user_role");
    return {
      "X-Tenant-ID": tenantId || "tef",
      "Content-Type": "application/json",
      ...(userId && { "X-User-ID": userId }),
      ...(userRole && { "X-User-Role": userRole }),
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [tenantId]);

  const persist = useCallback(
    async (nextRefs) => {
      setSaving(true);
      setError("");
      try {
        const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
          method: "PATCH",
          headers: getAuthHeaders(),
          body: JSON.stringify({ anah_references: nextRefs }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        const saved = Array.isArray(data.anah_references) ? data.anah_references : nextRefs;
        setRefs(saved);
        if (onUpdated) onUpdated(saved);
        toast.success("Références ANAH enregistrées");
      } catch (e) {
        setError(e.message || "Erreur réseau");
        toast.error(`Échec enregistrement : ${e.message || "réseau"}`);
      } finally {
        setSaving(false);
      }
    },
    [API, dossierId, getAuthHeaders, onUpdated]
  );

  const addRef = useCallback(() => {
    const v = (input || "").trim();
    if (!v) return;
    if (!REF_RE.test(v)) {
      setError("Format invalide — entre 4 et 10 chiffres");
      return;
    }
    if (refs.includes(v)) {
      setError("Référence déjà présente");
      return;
    }
    setInput("");
    persist([...refs, v]);
  }, [input, refs, persist]);

  const removeRef = useCallback(
    (ref) => {
      if (!window.confirm(`Supprimer la référence ${ref} ?`)) return;
      persist(refs.filter((r) => r !== ref));
    },
    [refs, persist]
  );

  return (
    <div
      className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-lg p-4"
      data-testid="anah-references-block"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-[var(--oh-success-text)]" />
          <h4 className="text-sm font-medium text-[var(--oh-text-secondary)] uppercase tracking-wider">
            Références ANAH ({refs.length})
          </h4>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-[var(--oh-success-text)]" />}
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="anah-references-badges">
        {refs.length === 0 ? (
          <p className="text-[var(--oh-text-muted)] text-sm italic">Aucune référence enregistrée</p>
        ) : (
          refs.map((ref, idx) => (
            <Badge
              key={ref}
              className={`bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] border-[var(--oh-success-border)] border font-mono text-sm pl-3 ${
                readOnly ? "pr-3" : "pr-1"
              } py-1 flex items-center gap-1`}
              data-testid={`anah-ref-badge-${idx}`}
            >
              {ref}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeRef(ref)}
                  disabled={saving}
                  className="ml-1 p-0.5 rounded hover:bg-[var(--oh-error-bg)] hover:text-[var(--oh-error-text)] disabled:opacity-40"
                  aria-label={`Supprimer ${ref}`}
                  data-testid={`anah-ref-remove-${idx}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </Badge>
          ))
        )}
      </div>

      {!readOnly && (
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Ex : 1085302"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addRef();
                }
              }}
              className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] font-mono"
              disabled={saving}
              data-testid="anah-ref-input"
            />
            {error && (
              <p className="text-xs text-[var(--oh-error-text)] mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {error}
              </p>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            onClick={addRef}
            disabled={saving || !input.trim()}
            className="bg-teal-600 hover:bg-teal-700 shrink-0"
            data-testid="anah-ref-add-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter
          </Button>
        </div>
      )}

      <p className="text-xs text-[var(--oh-text-muted)] mt-3">
        La première référence reste celle utilisée pour la liaison snapshot ANAH (sync, statut, dates).
        Les suivantes documentent les autres demandes du même bénéficiaire.
      </p>
    </div>
  );
}
